/**
 * Properties on a record page: grouped, typed, and edited with the input
 * that suits each type.
 *
 * View first, with empty properties tucked behind "Show empty fields" so a
 * long catalogue does not bury the few that are filled in. Edit shows every
 * property with its input; Save sends only what changed (a cleared one as
 * null), and the server merges, so two people editing different properties
 * do not overwrite each other.
 *
 * A lead's properties are read-only: they are the custom data it arrived
 * with, labelled and typed on read.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Alert, Anchor, Badge, Button, Card, Group, MultiSelect, NumberInput, Select, SimpleGrid, Stack, Switch,
  Text, TextInput, Textarea, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPencil } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import {
  useProperties, type PropertyDef, type PropertyObject, type SubmissionProperty,
} from '../../api/properties';
import { TOKENS } from '../../theme';
import { MonoLabel } from '../../ui';
import { useVocab } from '../../vocab';
import { useOwnerOptions } from '../crm/crmShared';
import {
  cleanInput, formatValue, groupDefs, isEmptyValue, sameValue, TYPE_LABEL,
} from './propertyModel';

type Values = Record<string, unknown>;

/** A stored value as its input holds it: pounds for money, `T` in datetimes. */
export function toInput(def: PropertyDef, stored: unknown): unknown {
  if (isEmptyValue(stored)) return def.type === 'multi_select' ? [] : '';
  switch (def.type) {
    case 'currency':
      return typeof stored === 'number' ? stored / 100 : stored;
    case 'datetime':
      return String(stored).replace(' ', 'T').slice(0, 16);
    case 'date':
      return String(stored).slice(0, 10);
    case 'boolean':
      return stored === true || stored === 'true' ? 'true' : 'false';
    case 'multi_select':
      return Array.isArray(stored) ? stored.map(String) : String(stored).split(',').map((s) => s.trim());
    default:
      return stored;
  }
}

/** The value to send for what an input holds. */
export function fromInput(def: PropertyDef, input: unknown): unknown {
  if (def.type === 'boolean') return input === 'true' ? true : input === 'false' ? false : null;
  return cleanInput(def.type, input);
}

/** One property's input, by type. */
export function PropertyInput({
  def, value, onChange, disabled,
}: {
  def: PropertyDef;
  /** As the input holds it (see toInput). */
  value: unknown;
  onChange: (next: unknown) => void;
  disabled?: boolean;
}) {
  const owners = useOwnerOptions(def.type === 'user');
  const common = {
    label: def.label,
    description: def.description || undefined,
    required: def.required,
    disabled,
    'data-testid': `prop-input-${def.key}`,
  };
  const text = typeof value === 'string' || typeof value === 'number' ? String(value) : '';
  switch (def.type) {
    case 'long_text':
      return <Textarea {...common} autosize minRows={2} value={text} onChange={(e) => onChange(e.currentTarget.value)} />;
    case 'number':
      return <NumberInput {...common} thousandSeparator="," value={text === '' ? '' : Number(text)} onChange={(n) => onChange(n)} />;
    case 'currency':
      return (
        <NumberInput
          {...common}
          prefix="£"
          thousandSeparator=","
          decimalScale={2}
          value={text === '' ? '' : Number(text)}
          onChange={(n) => onChange(n)}
        />
      );
    case 'date':
      return <TextInput {...common} type="date" value={text} onChange={(e) => onChange(e.currentTarget.value)} />;
    case 'datetime':
      return <TextInput {...common} type="datetime-local" value={text} onChange={(e) => onChange(e.currentTarget.value)} />;
    case 'boolean':
      return (
        <Select
          {...common}
          clearable
          placeholder="Not set"
          data={[{ value: 'true', label: 'Yes' }, { value: 'false', label: 'No' }]}
          value={text || null}
          onChange={(v) => onChange(v ?? '')}
        />
      );
    case 'select':
      return (
        <Select
          {...common}
          clearable
          searchable
          placeholder="Not set"
          data={def.options.map((o) => ({ value: o.value, label: o.label || o.value }))}
          value={text || null}
          onChange={(v) => onChange(v ?? '')}
        />
      );
    case 'multi_select':
      return (
        <MultiSelect
          {...common}
          clearable
          searchable
          placeholder="None"
          data={def.options.map((o) => ({ value: o.value, label: o.label || o.value }))}
          value={Array.isArray(value) ? value.map(String) : []}
          onChange={onChange}
        />
      );
    case 'user':
      return (
        <Select
          {...common}
          clearable
          searchable
          placeholder="Nobody"
          data={owners}
          value={text || null}
          onChange={(v) => onChange(v ?? '')}
        />
      );
    case 'email':
      return <TextInput {...common} type="email" value={text} onChange={(e) => onChange(e.currentTarget.value)} />;
    case 'phone':
      return <TextInput {...common} type="tel" value={text} onChange={(e) => onChange(e.currentTarget.value)} />;
    case 'url':
      return <TextInput {...common} type="url" placeholder="https://" value={text} onChange={(e) => onChange(e.currentTarget.value)} />;
    default:
      return <TextInput {...common} value={text} onChange={(e) => onChange(e.currentTarget.value)} />;
  }
}

/** A group's heading: a rule and its name, so it never reads as a field. */
function GroupHeading({ name, first }: { name: string; first: boolean }) {
  return (
    <Group gap={8} mt={first ? 0 : 4} wrap="nowrap">
      <Text size="sm" fw={600} style={{ flex: 'none' }}>{name}</Text>
      <div style={{ flex: 1, height: 1, background: TOKENS.border }} />
    </Group>
  );
}

/** A value as it reads, with links for email, phone and web addresses. */
export function PropertyValue({ def, value, names }: { def: PropertyDef; value: unknown; names?: Record<string, string> }) {
  const shown = formatValue(def, value, names);
  if (!shown) return <Text size="sm" c="dimmed">Not set</Text>;
  if (def.type === 'url') {
    return <Anchor size="sm" href={shown} target="_blank" rel="noreferrer" style={{ overflowWrap: 'anywhere' }}>{shown}</Anchor>;
  }
  if (def.type === 'email') return <Anchor size="sm" href={`mailto:${shown}`}>{shown}</Anchor>;
  if (def.type === 'phone') return <Anchor size="sm" href={`tel:${shown.replace(/\s/g, '')}`}>{shown}</Anchor>;
  if (def.type === 'select' || def.type === 'multi_select') {
    const list = def.type === 'select' ? [String(value)] : (Array.isArray(value) ? value.map(String) : [String(value)]);
    return (
      <Group gap={4} wrap="wrap">
        {list.map((x) => {
          const o = def.options.find((opt) => opt.value === x);
          return (
            <Badge key={x} size="sm" variant="light" color={o?.colour ?? 'gray'} radius="sm" tt="none">
              {o?.label || x}
            </Badge>
          );
        })}
      </Group>
    );
  }
  return <Text size="sm" style={{ overflowWrap: 'anywhere', whiteSpace: def.type === 'long_text' ? 'pre-wrap' : undefined }}>{shown}</Text>;
}

function Labelled({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <div style={{ minWidth: 0 }}>
      <MonoLabel style={{ marginBottom: 3 }}>{label}</MonoLabel>
      {children}
      {hint && <Text size="xs" c="dimmed">{hint}</Text>}
    </div>
  );
}

/**
 * A record's properties, grouped, with an Edit mode. `onSave` gets only the
 * changed keys, a cleared one as null.
 */
export function PropertiesPanel({
  object, values, canEdit, onSave, saving, title,
}: {
  object: Exclude<PropertyObject, 'submission'>;
  values: Values;
  canEdit: boolean;
  onSave: (patch: Values) => Promise<unknown>;
  saving?: boolean;
  title?: string;
}) {
  const v = useVocab();
  const all = useProperties(object, { archived: true });
  const defs = useMemo(() => (all.data?.properties ?? []).filter((d) => !d.archived), [all.data]);
  const known = useMemo(() => new Set((all.data?.properties ?? []).map((d) => d.key)), [all.data]);
  const owners = useOwnerOptions(defs.some((d) => d.type === 'user'));
  const names = useMemo(() => Object.fromEntries(owners.map((o) => [o.value, o.label.replace(/ \(you\)$/, '')])), [owners]);
  const [editing, setEditing] = useState(false);
  const [showEmpty, setShowEmpty] = useState(false);
  const [draft, setDraft] = useState<Values>({});
  const [error, setError] = useState('');

  useEffect(() => {
    if (!editing) setDraft(Object.fromEntries(defs.map((d) => [d.key, toInput(d, values[d.key])])));
  }, [defs, values, editing]);

  const undefinedKeys = Object.keys(values).filter((k) => !known.has(k) && !isEmptyValue(values[k]));
  const filled = defs.filter((d) => !isEmptyValue(values[d.key])).length;
  const groups = groupDefs(defs);

  function changes(): Values {
    const patch: Values = {};
    for (const d of defs) {
      const next = fromInput(d, draft[d.key]);
      if (!sameValue(next, values[d.key])) patch[d.key] = next;
    }
    return patch;
  }

  async function save() {
    const patch = changes();
    if (Object.keys(patch).length === 0) {
      setEditing(false);
      return;
    }
    setError('');
    try {
      await onSave(patch);
      setEditing(false);
      notifications.show({ color: 'teal', title: 'Saved', message: `${Object.keys(patch).length} changed` });
    } catch (e) {
      setError((e as Error).message);
    }
  }

  const manage = all.data?.can_manage ?? false;
  return (
    <Card p="md" data-testid={`properties-${object}`}>
      <Stack gap="sm">
        <Group justify="space-between" wrap="wrap">
          <Group gap={8}>
            <Text fw={600} size="sm">{title ?? v.Many('property')}</Text>
            {defs.length > 0 && <MonoLabel>{`${filled} of ${defs.length} filled in`}</MonoLabel>}
          </Group>
          <Group gap="sm">
            {!editing && defs.length > filled && (
              <Switch
                size="xs"
                label="Show empty fields"
                checked={showEmpty}
                onChange={(e) => setShowEmpty(e.currentTarget.checked)}
              />
            )}
            {canEdit && defs.length > 0 && !editing && (
              <Button size="compact-sm" variant="default" leftSection={<IconPencil size={13} />} onClick={() => setEditing(true)}>
                Edit
              </Button>
            )}
          </Group>
        </Group>
        {error && <Alert color="red" variant="light">{error}</Alert>}
        {all.isPending && <Text size="sm" c="dimmed">Loading…</Text>}
        {!all.isPending && defs.length === 0 && (
          <Text size="sm" c="dimmed">
            {`No ${v.many('property')} for ${objectLower(v, object)} yet.`}
            {manage && <>{' '}<Anchor component={Link} to={`/settings?tab=fields&object=${object}`} size="sm">Add some in Settings</Anchor></>}
          </Text>
        )}
        {groups.map(({ group, defs: list }, gi) => {
          const shown = editing || showEmpty ? list : list.filter((d) => !isEmptyValue(values[d.key]));
          if (shown.length === 0) return null;
          return (
            <Stack key={group || 'none'} gap={8}>
              {group && <GroupHeading name={group} first={gi === 0} />}
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" verticalSpacing={editing ? 'sm' : 12}>
                {shown.map((d) => (editing ? (
                  <PropertyInput
                    key={d.key}
                    def={d}
                    value={draft[d.key]}
                    onChange={(next) => setDraft((x) => ({ ...x, [d.key]: next }))}
                  />
                ) : (
                  <Labelled key={d.key} label={d.label}>
                    <PropertyValue def={d} value={values[d.key]} names={names} />
                  </Labelled>
                )))}
              </SimpleGrid>
            </Stack>
          );
        })}
        {!editing && defs.length > 0 && filled === 0 && !showEmpty && (
          <Text size="sm" c="dimmed">Nothing filled in yet.</Text>
        )}
        {!editing && undefinedKeys.length > 0 && (
          <Stack gap={8}>
            <Tooltip label="Kept as they arrived. Define them in Settings to give them a type." withArrow>
              <div><GroupHeading name="Not defined" first={false} /></div>
            </Tooltip>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" verticalSpacing={12}>
              {undefinedKeys.map((k) => (
                <Labelled key={k} label={k}>
                  <Text size="sm" style={{ overflowWrap: 'anywhere' }}>
                    {typeof values[k] === 'object' ? JSON.stringify(values[k]) : String(values[k])}
                  </Text>
                </Labelled>
              ))}
            </SimpleGrid>
          </Stack>
        )}
        {editing && (
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => { setEditing(false); setError(''); }}>Cancel</Button>
            <Button onClick={save} loading={saving}>Save</Button>
          </Group>
        )}
      </Stack>
    </Card>
  );
}

function objectLower(v: ReturnType<typeof useVocab>, o: PropertyObject): string {
  switch (o) {
    case 'person': return v.many('person');
    case 'company': return v.many('company');
    case 'deal': return v.many('deal');
    case 'submission': return v.many('lead');
  }
}

/**
 * The inputs a new record needs before it can be created: its required
 * properties. Nothing when there are none.
 */
export function RequiredPropertyInputs({
  object, values, onChange,
}: {
  object: Exclude<PropertyObject, 'submission'>;
  values: Values;
  onChange: (next: Values) => void;
}) {
  const defs = (useProperties(object).data?.properties ?? []).filter((d) => d.required && !d.archived);
  if (defs.length === 0) return null;
  return (
    <>
      {defs.map((d) => (
        <PropertyInput
          key={d.key}
          def={d}
          value={values[d.key] ?? toInput(d, null)}
          onChange={(next) => onChange({ ...values, [d.key]: next })}
        />
      ))}
    </>
  );
}

/** What RequiredPropertyInputs collected, as the create body's `properties`. */
export function requiredValues(defs: PropertyDef[] | undefined, values: Values): Values | undefined {
  const out: Values = {};
  for (const d of defs ?? []) {
    if (!(d.key in values)) continue;
    const v = fromInput(d, values[d.key]);
    if (!isEmptyValue(v)) out[d.key] = v;
  }
  return Object.keys(out).length ? out : undefined;
}

/**
 * A lead's properties, read-only: the organisation's labels and types over
 * the custom data it arrived with. A value that does not read as its type
 * is shown as it came, marked.
 */
export function SubmissionProperties({ properties }: { properties: SubmissionProperty[] }) {
  const v = useVocab();
  const [showEmpty, setShowEmpty] = useState(false);
  if (properties.length === 0) return null;
  const asDef = (p: SubmissionProperty, i: number): PropertyDef => ({
    id: p.key, object_type: 'submission', key: p.key, label: p.label, type: p.type, options: [],
    group_name: p.group_name, position: i, required: false, description: '', archived: false,
    created_at: '', updated_at: '', created_by: '',
  });
  const defs = properties.map(asDef);
  const byKey = new Map(properties.map((p) => [p.key, p]));
  const filled = properties.filter((p) => !isEmptyValue(p.value)).length;
  return (
    <div
      data-testid="lead-properties"
      style={{ border: `1px solid ${TOKENS.border}`, borderRadius: 10, padding: '12px 14px 14px', marginTop: 16 }}
    >
      <Group justify="space-between" mb={10} wrap="wrap">
        <Group gap={8}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{v.Many('property')}</div>
          <MonoLabel>{`${filled} of ${properties.length}`}</MonoLabel>
        </Group>
        {filled < properties.length && (
          <Switch size="xs" label="Show empty fields" checked={showEmpty} onChange={(e) => setShowEmpty(e.currentTarget.checked)} />
        )}
      </Group>
      <Stack gap="sm">
        {groupDefs(defs).map(({ group, defs: list }, gi) => {
          const shown = showEmpty ? list : list.filter((d) => !isEmptyValue(byKey.get(d.key)?.value));
          if (!shown.length) return null;
          return (
            <Stack key={group || 'none'} gap={6}>
              {group && <GroupHeading name={group} first={gi === 0} />}
              <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="md" verticalSpacing={12}>
                {shown.map((d) => {
                  const p = byKey.get(d.key);
                  return (
                    <Labelled
                      key={d.key}
                      label={d.label}
                      hint={p && !p.valid ? `Not ${TYPE_LABEL[d.type].toLowerCase()}, shown as it came` : undefined}
                    >
                      {p && !p.valid
                        ? <Text size="sm" style={{ overflowWrap: 'anywhere' }}>{p.raw}</Text>
                        : <PropertyValue def={d} value={p?.value} />}
                    </Labelled>
                  );
                })}
              </SimpleGrid>
            </Stack>
          );
        })}
        {filled === 0 && !showEmpty && <Text size="sm" c="dimmed">None of them filled in.</Text>}
      </Stack>
    </div>
  );
}
